/**
 * Where a part is plucked.
 *
 * A string player is told to pluck by a word over the staff -- `pizz.` -- and goes on
 * plucking until another word, `arco`, says to take the bow up again (`said.ts`).
 *
 * Only a bowed string plucks at the word. Written over a flute's staff it is a word, and
 * the flute goes on being blown.
 */

import type { Part, Score } from "../document/types";
import { holdingOf } from "./said";
import type { Holding } from "./said";

/** `pizz.`, `pizzicato`, `arco`, and either of them inside a longer instruction. */
const SAID = /\b(pizz|arco)/i;

/** General MIDI's bowed strings: the four of the quartet, and its two string sections. */
const BOWED: ReadonlySet<number> = new Set([40, 41, 42, 43, 48, 49]);

/**
 * General MIDI's one program for a plucked string of any size. It is what a MIDI file is
 * told; a played note keeps the program of its own instrument, which is still what is in
 * the player's hands.
 */
export const PIZZICATO_PROGRAM = 45;

/** Whether an instrument is one that is bowed, and so one that can be told to pluck. */
export function bowed(program: number | null): boolean {
  return program !== null && BOWED.has(program);
}

/** What a word says about the bow: put down, taken up, or nothing at all. */
function said(text: string): boolean | null {
  const found = SAID.exec(text);
  if (found === null) return null;
  return found[1]?.toLowerCase() === "pizz";
}

/** Where `part` is plucked, bar by bar of the written order. */
export function pluckingOf(score: Score, part: Part): Holding {
  return holdingOf(score, part, said);
}
